// ===== BEGIN FILE: src/components/report-v2/sections/OpportunityWindowsSection.tsx =====
/**
 * Upcoming Opportunity & Caution Windows — rendered after Dasha Timeline.
 * Shows feed-generated prediction windows grouped into Opportunities and Cautions,
 * with dates, life area, confidence %, and key evidence points.
 */

import React from 'react';
import { Box, Typography, Chip, LinearProgress } from '@mui/joy';
import { ReportSection } from '../ReportSection';
import type { ReportSectionProps } from '../types';

// ─── helpers ────────────────────────────────────────────────────────────────

type FeedWindow = NonNullable<ReportSectionProps['data']['feedWindows']>[number];

/** Parse "Title: rest of text" → { title, body } */
function parseMessage(message: string): { title: string; body: string } {
  const colonIdx = message.indexOf(':');
  if (colonIdx > 0 && colonIdx < 80) {
    const title = message.slice(0, colonIdx).trim();
    const body = message.slice(colonIdx + 1).trim();
    return { title, body };
  }
  // Fallback: first sentence as title
  const dotIdx = message.indexOf('. ');
  if (dotIdx > 0 && dotIdx < 100) {
    return { title: message.slice(0, dotIdx + 1).trim(), body: message.slice(dotIdx + 1).trim() };
  }
  return { title: message.slice(0, 80), body: '' };
}

function fmtDate(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
  } catch {
    return iso;
  }
}

/** Convert raw score to a 0-100 confidence figure */
function toConfidence(score: number): number {
  return Math.round(Math.min(95, 50 + Math.abs(score) * 0.45));
}

function confidenceLabel(pct: number): string {
  if (pct >= 75) return 'High';
  if (pct >= 55) return 'Medium';
  return 'Moderate';
}

function lifeAreaLabel(area: string): string {
  const MAP: Record<string, string> = {
    Self: 'Health / Self',
    Career: 'Career',
    Wealth: 'Wealth',
    Health: 'Health',
    Spouse: 'Spouse / Relationships',
    Children: 'Children',
    Mother: 'Mother',
    Father: 'Father',
    Property: 'Property',
    Travel: 'Travel',
    Investments: 'Investments',
    Litigation: 'Litigation',
    Spirituality: 'Spirituality',
    AccidentRisk: 'Accident Risk',
  };
  return MAP[area] || area;
}

// ─── sub-components ─────────────────────────────────────────────────────────

interface WindowCardProps {
  item: FeedWindow;
  kind: 'opportunity' | 'caution';
  className?: string;
}

const OPPORTUNITY_COLOR = '#16a34a';
const CAUTION_COLOR = '#d97706';
const ALERT_COLOR = '#dc2626';

const WindowCard: React.FC<WindowCardProps> = ({ item, kind, className }) => {
  const { title, body } = parseMessage(item.message);
  const confidence = toConfidence(item.score);
  const confLabel = confidenceLabel(confidence);
  const accentColor =
    kind === 'opportunity' ? OPPORTUNITY_COLOR :
    item.severity === 'alert' ? ALERT_COLOR : CAUTION_COLOR;

  // Show top 2 evidence points only (keeps cards compact)
  const evidenceSnippets = (item.evidence || [])
    .filter(e => e && e.trim().length > 0)
    .slice(0, 2);

  return (
    <Box
      className={className}
      sx={{
        p: 1.75,
        mb: 1.5,
        borderRadius: '10px',
        border: '1px solid',
        borderColor: kind === 'opportunity' ? 'rgba(22,163,74,0.25)' : 'rgba(217,119,6,0.25)',
        borderLeft: `4px solid ${accentColor}`,
        background: kind === 'opportunity'
          ? 'linear-gradient(135deg, rgba(22,163,74,0.03) 0%, rgba(255,255,255,0) 100%)'
          : 'linear-gradient(135deg, rgba(217,119,6,0.04) 0%, rgba(255,255,255,0) 100%)',
        breakInside: 'avoid',
        pageBreakInside: 'avoid',
      }}
    >
      {/* Row 1: Life area chip + severity badge */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.75, flexWrap: 'wrap', gap: 0.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <Box
            sx={{
              px: 1, py: 0.2,
              borderRadius: '20px',
              fontSize: '0.65rem',
              fontWeight: 700,
              letterSpacing: '0.04em',
              textTransform: 'uppercase',
              color: accentColor,
              border: `1px solid ${accentColor}`,
              '@media print': { fontSize: '6pt' },
            }}
          >
            {lifeAreaLabel(item.lifeArea)}
          </Box>
        </Box>
        <Box
          sx={{
            px: 1, py: 0.2,
            borderRadius: '20px',
            fontSize: '0.65rem',
            fontWeight: 700,
            background: kind === 'opportunity' ? 'rgba(22,163,74,0.12)' : 'rgba(217,119,6,0.12)',
            color: accentColor,
            '@media print': { fontSize: '6pt' },
          }}
        >
          {kind === 'opportunity' ? '✨ Opportunity' : item.severity === 'alert' ? '🔴 Alert' : '⚠️ Caution'}
        </Box>
      </Box>

      {/* Row 2: Title */}
      <Typography
        sx={{
          fontWeight: 700,
          fontSize: '0.85rem',
          lineHeight: 1.3,
          color: '#1a1a2e',
          mb: 0.5,
          '@media print': { fontSize: '8.5pt' },
        }}
      >
        {title}
      </Typography>

      {/* Row 3: Body text */}
      {body && (
        <Typography
          sx={{
            fontSize: '0.75rem',
            color: 'text.secondary',
            lineHeight: 1.5,
            mb: 0.75,
            '@media print': { fontSize: '7.5pt' },
          }}
        >
          {body.length > 200 ? body.slice(0, 200) + '…' : body}
        </Typography>
      )}

      {/* Row 4: Date window */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.75, flexWrap: 'wrap' }}>
        <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary', fontWeight: 600, '@media print': { fontSize: '7pt' } }}>
          📅 Window:
        </Typography>
        <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: '#1a1a2e', '@media print': { fontSize: '7pt' } }}>
          {fmtDate(item.window.from)} — {fmtDate(item.window.to)}
        </Typography>
      </Box>

      {/* Row 5: Confidence bar */}
      <Box sx={{ mb: evidenceSnippets.length > 0 ? 0.75 : 0 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.35 }}>
          <Typography sx={{ fontSize: '0.65rem', color: 'text.secondary', '@media print': { fontSize: '6.5pt' } }}>
            Confidence
          </Typography>
          <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, color: accentColor, '@media print': { fontSize: '6.5pt' } }}>
            {confLabel} — {confidence}%
          </Typography>
        </Box>
        <Box
          sx={{
            height: '5px',
            borderRadius: '4px',
            background: '#e5e7eb',
            overflow: 'hidden',
            '@media print': { height: '3pt', background: '#e5e7eb !important' },
          }}
        >
          <Box
            sx={{
              height: '100%',
              width: `${confidence}%`,
              background: accentColor,
              borderRadius: '4px',
            }}
          />
        </Box>
      </Box>

      {/* Row 6: Evidence snippets */}
      {evidenceSnippets.length > 0 && (
        <Box sx={{ mt: 0.5 }}>
          {evidenceSnippets.map((e, i) => (
            <Typography
              key={i}
              sx={{
                fontSize: '0.62rem',
                color: 'text.tertiary',
                lineHeight: 1.4,
                '&::before': { content: '"• "' },
                '@media print': { fontSize: '6pt' },
              }}
            >
              {e}
            </Typography>
          ))}
        </Box>
      )}
    </Box>
  );
};

// ─── main section ────────────────────────────────────────────────────────────

export const OpportunityWindowsSection: React.FC<ReportSectionProps> = ({ data, onNavigate }) => {
  const rawWindows = data.feedWindows ?? [];
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  // Only show windows that haven't fully passed yet
  const upcoming = rawWindows
    .filter(f => {
      if (!f.window?.to) return false;
      try {
        return new Date(f.window.to) >= today;
      } catch {
        return false;
      }
    })
    .sort((a, b) => {
      try {
        return new Date(a.window.from).getTime() - new Date(b.window.from).getTime();
      } catch {
        return 0;
      }
    });

  const opportunities = upcoming.filter(f => f.severity === 'positive' || (f.severity === 'mixed' && f.score > 0));
  const cautions = upcoming.filter(f => f.severity === 'caution' || f.severity === 'alert' || (f.severity === 'mixed' && f.score <= 0));

  if (upcoming.length === 0) {
    return (
      <ReportSection
        title="Upcoming Opportunity & Caution Windows"
        icon="🔮"
        pageBreakBefore
        onNavigate={onNavigate ? () => onNavigate('feeds') : undefined}
        navigateLabel="View All Predictions →"
        navigateTab="feeds"
      >
        <Box sx={{ p: 3, textAlign: 'center', border: '1px solid', borderColor: 'divider', borderRadius: 'md' }}>
          <Typography level="body-md" sx={{ color: 'text.secondary' }}>
            No upcoming prediction windows calculated for this chart.
          </Typography>
          <Typography level="body-sm" sx={{ color: 'text.secondary', mt: 1 }}>
            Prediction windows are generated from your current Vimshottari Dasha period and transit analysis.
          </Typography>
        </Box>
      </ReportSection>
    );
  }

  const oppToShow = opportunities.slice(0, 10);
  const cautToShow = cautions.slice(0, 8);

  return (
    <ReportSection
      title="Upcoming Opportunity & Caution Windows"
      icon="🔮"
      pageBreakBefore
      onNavigate={onNavigate ? () => onNavigate('feeds') : undefined}
      navigateLabel="View All Predictions →"
      navigateTab="feeds"
    >
      {/* Intro */}
      <Box sx={{ mb: 3 }}>
        <Typography level="body-md" sx={{ color: 'text.secondary', lineHeight: 1.7 }}>
          These time windows are computed from your current Vimshottari Dasha period combined with real-time
          planetary transits. Opportunity windows show when planetary support aligns with key life houses.
          Caution windows indicate periods of increased resistance or challenge in specific areas.
        </Typography>
      </Box>

      {/* Summary pills */}
      <Box sx={{ display: 'flex', gap: 1.5, mb: 3, flexWrap: 'wrap' }}>
        <Box
          sx={{
            px: 2, py: 0.75,
            borderRadius: '20px',
            background: 'rgba(22,163,74,0.1)',
            border: '1px solid rgba(22,163,74,0.3)',
            display: 'flex', alignItems: 'center', gap: 0.75,
          }}
        >
          <Typography sx={{ fontSize: '1.1rem' }}>✨</Typography>
          <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: OPPORTUNITY_COLOR }}>
            {oppToShow.length} Opportunity Window{oppToShow.length !== 1 ? 's' : ''}
          </Typography>
        </Box>
        <Box
          sx={{
            px: 2, py: 0.75,
            borderRadius: '20px',
            background: 'rgba(217,119,6,0.1)',
            border: '1px solid rgba(217,119,6,0.3)',
            display: 'flex', alignItems: 'center', gap: 0.75,
          }}
        >
          <Typography sx={{ fontSize: '1.1rem' }}>⚠️</Typography>
          <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: CAUTION_COLOR }}>
            {cautToShow.length} Caution Window{cautToShow.length !== 1 ? 's' : ''}
          </Typography>
        </Box>
      </Box>

      {/* Opportunities */}
      {oppToShow.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
            <Box sx={{ width: 3, height: 18, borderRadius: '2px', background: OPPORTUNITY_COLOR, flexShrink: 0 }} />
            <Typography
              level="title-md"
              sx={{ fontWeight: 700, color: OPPORTUNITY_COLOR, fontSize: '0.95rem', '@media print': { fontSize: '9pt' } }}
            >
              ✨ Opportunity Windows
            </Typography>
          </Box>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5 }}>
            {oppToShow.map(item => (
              <WindowCard key={item.id} item={item} kind="opportunity" className={item.private ? 'no-export' : undefined} />
            ))}
          </Box>
        </Box>
      )}

      {/* Cautions */}
      {cautToShow.length > 0 && (
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
            <Box sx={{ width: 3, height: 18, borderRadius: '2px', background: CAUTION_COLOR, flexShrink: 0 }} />
            <Typography
              level="title-md"
              sx={{ fontWeight: 700, color: CAUTION_COLOR, fontSize: '0.95rem', '@media print': { fontSize: '9pt' } }}
            >
              ⚠️ Caution Windows
            </Typography>
          </Box>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5 }}>
            {cautToShow.map(item => (
              <WindowCard key={item.id} item={item} kind="caution" className={item.private ? 'no-export' : undefined} />
            ))}
          </Box>
        </Box>
      )}

      {/* Disclaimer */}
      <Box
        sx={{
          mt: 3, p: 1.5,
          borderRadius: '8px',
          background: 'rgba(91,33,182,0.04)',
          border: '1px solid rgba(91,33,182,0.12)',
        }}
      >
        <Typography sx={{ fontSize: '0.68rem', color: 'text.secondary', lineHeight: 1.6, '@media print': { fontSize: '6.5pt' } }}>
          ℹ️ <strong>Guidance:</strong> These windows indicate periods of heightened astrological influence —
          they represent tendencies, not certainties. The confidence score reflects the strength of planetary
          alignments in your chart. Use them to plan and prepare, not to predict rigid outcomes.
          Consult a qualified astrologer for major life decisions.
        </Typography>
      </Box>
    </ReportSection>
  );
};
